import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Button } from '@/components/ui/button';
import { CharacterCount } from '@/components/CharacterCount';
import { COLLAB_MESSAGE_MAX } from '../limits';
import type { CommentTarget } from '../collabTypes';
import { formatDayLabel, formatServerDateShort, formatThreadTime, serverDayKey } from '@/lib/dates';
import { cn } from '@/lib/utils';
import type { ReviewThreadEntry } from '../collabTypes';

function initial(name: string | null): string {
  const t = (name ?? '').trim();
  return t ? t[0]!.toUpperCase() : '?';
}

// One colour per person rather than one colour for everyone: in a two-party thread the avatars
// were identical, so the column told you nothing. Picked from the address, so a person keeps
// their colour across sessions and machines without anything being stored.
const AVATAR_TONES = ['bg-avatar-1', 'bg-avatar-2', 'bg-avatar-3', 'bg-avatar-4', 'bg-avatar-5'] as const;

function toneFor(key: string | null): string {
  const t = (key ?? '').trim().toLowerCase();
  let h = 0;
  for (let i = 0; i < t.length; i += 1) h = (h * 31 + t.charCodeAt(i)) % 1000003;
  return AVATAR_TONES[h % AVATAR_TONES.length]!;
}

/** Initial-circle avatar matching the app's user chip. Exported so the composer
 *  (on the review page) can show the current user with the same treatment. */
export function ThreadAvatar({ name, className }: { name: string | null; className?: string }) {
  return (
    <div
      className={cn(
        'flex size-8 shrink-0 select-none items-center justify-center rounded-full',
        toneFor(name),
        'text-[13px] font-bold text-white',
        className,
      )}
      aria-hidden
    >
      {initial(name)}
    </div>
  );
}

/** Your own messages read as "You" — in a two-person thread where the people are called things
 *  like "Editor" and "Test", seeing your own words under someone's name is a real stumble. The
 *  Activity trail and the CSV export keep the actual names, so the record is unaffected. */
function speaker(entry: ReviewThreadEntry, currentEmail: string | null): string {
  if (currentEmail && entry.author_email === currentEmail) return 'You';
  return entry.author_name || entry.author_email || 'Unknown';
}

function Meta({ entry, showWho, currentEmail }: {
  entry: ReviewThreadEntry; showWho: boolean; currentEmail: string | null;
}) {
  const name = speaker(entry, currentEmail);
  // A run of messages from the same person names them once — five identical name+role rows in a
  // column this narrow is noise, not information.
  // The time sits in its own right-hand column on every row, so the timestamps line up and the
  // left edge is just names and words. Inline after the name they crowded the message.
  const when = (
    <time
      dateTime={entry.at}
      title={formatServerDateShort(entry.at)}
      className="shrink-0 text-[11.5px] tabular-nums text-muted/70"
    >
      {formatThreadTime(entry.at)}
    </time>
  );
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="flex min-w-0 items-baseline gap-2">
        {showWho && <span className="truncate text-[13px] font-semibold text-navy">{name}</span>}
      </span>
      {when}
    </div>
  );
}

/** What a message is about, when it is about one tracked change. A quote, not an input: the
 *  grey filled box read as an empty text field beside the composer's own chip. */
function ChangeRef({ text }: { text: string }) {
  return (
    <p className="mt-1 border-l-2 border-line-strong pl-2.5 text-[12px] text-muted">
      On <span className="text-navy/80">{text}</span>
    </p>
  );
}

function ChatEntry({ entry, showWho, currentEmail }: { entry: ReviewThreadEntry; showWho: boolean; currentEmail: string | null }) {
  return (
    // Every message sits at the same indent. Nesting replies produced a ragged left edge and a
    // ladder of stub rules for no gain — with two parties and a divider between turns, who is
    // answering whom is already obvious. The reply's own label carries the rest.
    <div className="flex gap-3">
      {showWho ? (
        // The avatar keeps the person's own initial even when the name reads "You".
        <ThreadAvatar name={entry.author_name || entry.author_email} />
      ) : (
        <div className="size-8 shrink-0" aria-hidden />
      )}
      <div className="min-w-0 flex-1">
        <Meta entry={entry} showWho={showWho} currentEmail={currentEmail} />
        {entry.change_text && <ChangeRef text={entry.change_text} />}
        <p className="mt-1 whitespace-pre-wrap [overflow-wrap:anywhere] text-[13.5px] leading-relaxed text-navy">{entry.body}</p>
      </div>
    </div>
  );
}

function RejectEntry({ entry, currentEmail }: { entry: ReviewThreadEntry; currentEmail: string | null }) {
  const name = speaker(entry, currentEmail);
  // What the send-back DID, not just what was said about it.
  const tally = [
    entry.accepted ? `${entry.accepted} accepted` : '',
    entry.rejected ? `${entry.rejected} sent back to rework` : '',
  ].filter(Boolean).join(' · ');
  return (
    <div className="rounded-xl border border-line bg-line-soft/50 px-4 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 text-[12.5px] font-semibold text-navy">
          Sent back for changes <span className="font-normal text-muted">by {name}</span>
        </span>
        <time
          dateTime={entry.at}
          title={formatServerDateShort(entry.at)}
          className="shrink-0 text-[11.5px] tabular-nums text-muted/70"
        >
          {formatThreadTime(entry.at)}
        </time>
      </div>
      {tally && <p className="mt-0.5 text-[12px] text-muted">{tally}</p>}
      {entry.body && (
        <p className="mt-1.5 whitespace-pre-wrap [overflow-wrap:anywhere] text-[13.5px] leading-relaxed text-navy">{entry.body}</p>
      )}
      {entry.comments.length > 0 && (
        <ul className="mt-2 space-y-1 border-t border-line pt-2">
          {entry.comments.map((c, i) => (
            <li key={`${c.path}-${i}`} className="text-[13px] text-navy">
              <span className="font-semibold">{c.field}:</span>{' '}
              <span className="text-muted [overflow-wrap:anywhere]">{c.comment}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The review conversation on a working copy — reviewer comments, editor replies
 *  (nested), and send-backs, oldest first, as a proper feed with avatars + names.
 *  ``composer`` docks at the bottom of the same card (the comment / reply input for
 *  the current user). Renders nothing only when there's neither history nor composer. */
export function ReviewThread({
  entries,
  composer,
  fill = false,
  currentEmail = null,
}: {
  entries: ReviewThreadEntry[];
  composer?: ReactNode;
  /** The signed-in user, so their own messages read as "You". */
  currentEmail?: string | null;
  /** Fill the height of a drawer: the messages scroll, the header and composer stay put. Without
   *  it the whole card grows and the composer is pushed out of reach on a long thread. */
  fill?: boolean;
}) {
  const count = entries.length;
  // A conversation reads oldest-first, so the newest message is at the BOTTOM — open there, and
  // follow new arrivals. Not while the reader is scrolled up in the history: yanking them away
  // mid-sentence because the other person typed is worse than a message they have to scroll to.
  const listRef = useRef<HTMLOListElement>(null);
  const atBottom = useRef(true);
  const opened = useRef(false);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (!opened.current || atBottom.current) {
      el.scrollTop = el.scrollHeight;
      opened.current = true;
    }
  }, [count]);
  const onScroll = () => {
    const el = listRef.current;
    if (el) atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  };
  if (entries.length === 0 && !composer) return null;
  return (
    <div className={cn(
      'overflow-hidden rounded-2xl border border-line bg-white shadow-card',
      fill && 'flex min-h-0 flex-1 flex-col',
    )}>
      <div className="shrink-0 border-b border-line-soft px-5 py-3.5">
        <h3 className="text-[14px] font-semibold text-navy">Discussion</h3>
        <p className="text-[12px] text-muted">
          {count === 0
            ? 'No comments yet — it stays in review'
            : `${count} ${count === 1 ? 'message' : 'messages'} · stays in review`}
        </p>
      </div>

      {count > 0 && (
        <ol ref={listRef} onScroll={onScroll} className={cn('px-5 py-4', fill && 'min-h-0 flex-1 overflow-y-auto')}>
          {entries.map((entry, i) => {
            const prev = entries[i - 1];
            // A day separator instead of a date on every line: the thread says which day once,
            // where the day changes.
            const newDay = !prev || serverDayKey(prev.at) !== serverDayKey(entry.at);
            // Name the author once per run: same person, same kind, nothing structural between.
            const showWho = !prev || prev.kind === 'reject' || prev.kind !== entry.kind
              || (prev.author_email ?? '') !== (entry.author_email ?? '');
            return (
              // A hairline between turns, nothing between a person's consecutive messages: the
              // divider marks who is speaking, so it lands where the speaker changes.
              <li key={i} className={cn(
                showWho && i > 0 && !newDay && 'mt-4 border-t border-line-soft pt-4',
                !showWho && 'mt-2.5',
              )}>
                {newDay && i > 0 && (
                  <p className="mb-4 mt-5 text-center text-[11px] font-semibold uppercase tracking-wide text-muted/70">
                    {formatDayLabel(entry.at)}
                  </p>
                )}
                {entry.kind === 'reject'
                  ? <RejectEntry entry={entry} currentEmail={currentEmail} />
                  : <ChatEntry entry={entry} showWho={showWho} currentEmail={currentEmail} />}
              </li>
            );
          })}
        </ol>
      )}

      {composer && (
        <div className={cn('shrink-0 bg-line-soft/20 px-5 py-4', count > 0 && 'border-t border-line-soft')}>
          {composer}
        </div>
      )}
    </div>
  );
}

/** The message box docked at the bottom of the discussion — the current user's avatar beside a
 *  compact textarea, matching the thread's feed style.
 *
 *  ONE wording, on every screen and for every role. It used to name the reader and the act —
 *  "Add a comment for the editor" / "Comment", "Reply to the reviewer" / "Reply" — which meant
 *  four labels to keep straight, and the author's own editor read "Reply to the reviewer" even
 *  when nobody had written yet. It is one conversation about one working copy, so it says so.
 *
 *  The half-written message lives HERE, not on the page. Held on the page, every character
 *  re-rendered it — and the page owns the Word editor, so each keystroke re-rendered the whole
 *  document surface. Measured on a 22-page report: 11ms of blocked time per character, and it
 *  grows with the document. A draft nobody has sent is nobody else's business anyway.
 *
 *  `onSubmit` hands the text over and the box clears only once it resolves, so a message that
 *  fails to send is still there to try again.
 *
 *  `draftRef` is how the message outlives the box. The drawer unmounts when it is closed, and
 *  closing it to re-read the document mid-sentence is an ordinary thing to do — so the text is
 *  mirrored into a ref the screen holds. A ref, not state: writing to it re-renders nothing,
 *  which is the whole point of keeping the draft out of the page. */
export function ThreadComposer({
  name, onSubmit, pending, target, onClearTarget, draftRef,
}: {
  name: string | null;
  onSubmit: (text: string) => void | Promise<void>;
  pending: boolean;
  /** The change this comment is about (from the rail's Comment button); null = general. */
  target?: CommentTarget | null;
  onClearTarget?: () => void;
  /** Where the unsent message is kept while the drawer is shut. */
  draftRef?: RefObject<string>;
}) {
  const [value, setValue] = useState(() => draftRef?.current ?? '');
  const write = (next: string) => {
    setValue(next);
    if (draftRef) draftRef.current = next;
  };
  const send = () => {
    const text = value.trim();
    if (!text) return;
    void Promise.resolve(onSubmit(text)).then(() => write('')).catch(() => {});
  };
  return (
    <div className="flex gap-3">
      <ThreadAvatar name={name} />
      <div className="min-w-0 flex-1 space-y-2">
        {target && (
          <div className="flex items-center gap-2 border-l-2 border-line-strong pl-2.5 text-[12px] text-muted">
            <span className="truncate">On <span className="text-navy/80">{target.changeText}</span></span>
            {onClearTarget && (
              <button type="button" onClick={onClearTarget} className="ml-auto shrink-0 text-muted hover:text-navy" aria-label="Write about the whole document instead">
                ×
              </button>
            )}
          </div>
        )}
        <textarea
          value={value}
          onChange={(e) => write(e.target.value)}
          rows={3}
          maxLength={COLLAB_MESSAGE_MAX}
          placeholder="Add a message…"
          className="w-full resize-y rounded-lg border border-line bg-white px-3 py-2 text-[13px] leading-relaxed focus:border-electric focus:outline-none"
        />
        <div className="flex items-center justify-between gap-3">
          <CharacterCount value={value} max={COLLAB_MESSAGE_MAX} />
          <Button size="sm" disabled={!value.trim() || pending} onClick={send}>
            {pending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  );
}
